<template>
  <div>
    <!--
      Staff-only note. An impersonating admin is deliberately sent to THIS form rather than the
      softphone one, however the account's own agents work - a softphone campaign's destination is
      composed from the acting user, so one an admin created would ring an agent id that has no
      Telnyx credential. Says so here, because the swap is silent otherwise: the sidebar shows the
      same "Create Campaign" label either way.
    -->
    <b-alert
      v-if="showSoftphoneAdminNotice"
      show
      variant="info"
      class="p-2 mx-2"
      data-cy="softphone-admin-create-notice"
    >
      You are viewing this account as an admin, so this is the standard campaign form. Softphone
      campaigns have to be created by the agent they ring, and cannot be created on an account's
      behalf.
    </b-alert>

    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(showConfirmSubmit)"
      >
        <MainSection
          :categories="categoryList"
          :countries="countries"
          @category-changed="onCategoryChanged"
        />
        
        <Destination />

        <DestinationSchedulingCapacity />

        <AlertProgrammaticTarget
          class="mx-2"
          :routing-type="buyerCampaign.routing.type"
        />

        <TrafficSources :traffic-sources="allowedTrafficSources" />

        <Audience />

        <!-- Integrations is for configuring Pixels, but maybe not required for MVP: Adam -->
        <!-- <Integrations /> -->

        <Budget
          :invalid="invalid"
          :minimum-initial-deposit-satisfied="isMinimumInitialDepositRequirementSatisfied"
          :loading="isLoading"
          :selected-category="selectedCategory"
          @submit="showConfirmSubmit"
        />
      </b-form>
    </validation-observer>

    <b-modal
      id="confirm-create-campaign-modal"
      ref="confirm-create-campaign-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Confirm Campaign Creation"
      button-size="sm"
      no-close-on-backdrop
      @hidden="termsAccepted = false"
    >
      <p>
        Agents must service all qualified calls in good faith. Valid calls disconnected or rejected to avoid payment or due to agent mishandling may be deemed acceptable and billable. Repeated violations may result in force-billing, suspension, or termination of service.
      </p>

      <b-form-checkbox
        v-model="termsAccepted"
        data-cy="checkbox-accept-terms"
        class="mb-0"
      >
        I acknowledge and accept the above terms regarding the handling of qualified calls and the potential consequences of non-compliance.
      </b-form-checkbox>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="$bvModal.hide('confirm-create-campaign-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoading"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          :disabled="!termsAccepted"
          data-cy="button-confirm-create-campaign"
          @click="submitForm"
        >
          Confirm
        </BaseButtonLoader>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import MainSection from '../campaign/MainSection.vue'
import Destination from '../campaign/Destination.vue'
import DestinationSchedulingCapacity from '../campaign/DestinationSchedulingCapacity.vue'
import TrafficSources from '../campaign/TrafficSources.vue'
import Audience from './Audience.vue'
import Budget from './Budget.vue'
import AlertProgrammaticTarget from './AlertProgrammaticTarget.vue'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import { ACCOUNT_REQUIREMENTS, dashboardNotificationForAccountRequirementsMessages } from '@/store/modules/auth.store'
import { getSelectableCategories } from '@/utils/campaignCategories'

export default {
  name: 'BuyerCreateCampaign',

  title: 'Create campaign',

  components: {
    MainSection,
    Destination,
    DestinationSchedulingCapacity,
    TrafficSources,
    Audience,
    Budget,
    AlertProgrammaticTarget,
  },

  props: {
    id: {
      type: String,
      default: ''
    },
  },

  data() {
    return {
      isLoading: false,
      selectedCategory: null,
      termsAccepted: false
    }
  },

  computed: {
    ...mapState('common-entities', ['countries', 'categories']),

    ...mapGetters('account', ['allowedTrafficSources', 'allowedCategories']),

    ...mapState('campaign', ['buyerCampaign']),

    ...mapGetters('auth', ['isMinimumInitialDepositRequirementSatisfied', 'isImpersonating']),

    ...mapGetters('globalSettings', ['canViewSoftphoneCampaigns']),

    // Staff only, and only while the softphone feature is live - on an environment with
    // SOFTPHONE_GOES_LIVE off there is no softphone form to have been sent here instead of.
    showSoftphoneAdminNotice() {
      return this.isImpersonating && this.canViewSoftphoneCampaigns
    },

    categoryList() {
      return getSelectableCategories(this.allowedCategories, this.categories)
        .sort((a, b) => a.name.localeCompare(b.name))
    },

  },

  watch: {
    isMinimumInitialDepositRequirementSatisfied: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.showDashboardNotification(dashboardNotificationForAccountRequirementsMessages[ACCOUNT_REQUIREMENTS.MinimumInitialDeposit])
        } else {
          this.hideDashboardNotification()
        }
      }
    }
  },

  async created() {
    this.setDefaultBuyerCampaign()

    // Read before the form loads, and first: this route is deliberately unguarded (see
    // router/index.js), so unlike the softphone pages nothing upstream has fetched the setting on
    // a direct URL or a bookmark. The action caches for 15 minutes and swallows its own failures,
    // resolving false - which just leaves the note hidden.
    await this.getSoftphoneGoesLiveSetting()

    await this.getCountries()
    await this.getCategories()
  },

  methods: {
    ...mapActions([
      'showDashboardNotification',
      'hideDashboardNotification'
    ]),

    ...mapActions('common-entities', ['getCountries','getCategories']),

    ...mapActions('globalSettings', ['getSoftphoneGoesLiveSetting']),

    ...mapActions('campaign', ['setDefaultBuyerCampaign', 'createBuyerCampaign', 'fetchBuyerCampaign']),

    onCategoryChanged(category) {
      this.selectedCategory = category
    },

    showConfirmSubmit() {
      this.termsAccepted = false
      this.$bvModal.show('confirm-create-campaign-modal')
    },

    async submitForm() {
      try {
        this.isLoading = true

        await this.createBuyerCampaign(this.buyerCampaign)

        this.$bvModal.hide('confirm-create-campaign-modal')

        // TODO: Show a detailed message about the approval process
        // and redirect to campaigns list page
        showSuccessMessage('Campaign created successfully')
        this.$router.push({ name: 'ListBuyerCampaigns' })
      } catch (error) {
        showErrorMessage(error.response?.data?.title)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
